import React from 'react';
import { twMerge } from 'tailwind-merge';
import { LANDSCAPE_PHONE_MAX_HEIGHT } from 'flow/utils/meeting-constants';
import { selectActiveMeetingViewIsMinimized } from 'flow/selectors/meeting-selectors';
import {
    selectVideoMembers,
    selectVideoSenderScreenShareActive,
    selectVideoShowIncomingVideo,
} from 'flow/selectors/video-selectors';
import { useVideoGrid } from 'flow/hooks/useVideoGrid';
import { useMemberManagement } from 'flow/hooks/useMemberManagement';
import useVideoSubscriptions from 'flow/hooks/useVideoSubscriptions';
import useScreenSize from 'flow/hooks/useScreenSize';
import { memberDisplayName } from './main-area/meeting-tile-utils';
import { getSubscribableMemberNames } from 'flow/utils/video-subscription-utils';
// Dev-only: uncomment (with ENABLE_MOCK_PARTICIPANTS in meeting-mock, and
// <MockSpeakerToggleOverlay /> in the render) to drive the layout engine with a
// fixed mock participant set. Left commented so production never invokes it.
// import useMockParticipants from 'flow/hooks/useMockParticipants';
import SenderAloneView from './SenderAloneView';
import MeetingMinimizedMembers from './MeetingMinimizedMembers';
import MeetingMainArea from './main-area/MeetingMainArea';
import { useAppSelector } from 'flow/hooks/useRedux';

const MeetingVideoGrid: React.FC = () => {
    const isMinimizedView = useAppSelector(selectActiveMeetingViewIsMinimized);
    const senderScreenShareActive = useAppSelector(
        selectVideoSenderScreenShareActive,
    );
    const videoMembers = useAppSelector(selectVideoMembers);
    const showIncomingVideo = useAppSelector(selectVideoShowIncomingVideo);

    const {
        numberOfMembersToRender,
        isFullscreenOrNarrow,
        isNarrowWindow,
        isThumbnailView,
        shouldShowSenderInGrid,
        ownName,
        meetingMembers,
    } = useVideoGrid();

    const {
        screenSharesToRender,
        nonScreenSharesToRender,
        totalFeedMemberCount,
    } = useMemberManagement(meetingMembers, ownName);
    const allMembersToRender = screenSharesToRender
        ? screenSharesToRender.concat(nonScreenSharesToRender)
        : nonScreenSharesToRender;

    // Subscribe only to the members we actually render (the GRID_LIMIT-capped
    // set, overflow excluded) rather than to every participant. Driven from here
    // — the single owner of the capped set, mounted for the whole meeting and
    // independent of which view (full/dock/minimized) is showing. Debounce and
    // rate-limit policy lives in the manager the hook wraps. Pass the FULL
    // rendered set — screen shares included — because a sharing member's screen
    // rides their PRIMARY receive peer; excluding them would tear the on-stage
    // screen down / never subscribe it.
    //
    // Which of the rendered names are actually subscribable — the signaling-
    // registration gate (join-race protection, dial-in exclusion) and the
    // "Show incoming video" camera suppression (VID-53) — is decided by
    // getSubscribableMemberNames; see its doc for the full rationale.
    const visibleMemberNames = getSubscribableMemberNames(
        allMembersToRender.map((member) => memberDisplayName(member)),
        videoMembers,
        showIncomingVideo,
    );
    useVideoSubscriptions(visibleMemberNames);

    // Dev-only: injects a fixed mock participant set for layout / active-speaker
    // testing (see the commented import above). Left commented in committed code.
    // useMockParticipants();

    const { height: viewportHeight, width: viewportWidth } = useScreenSize();
    const isLandscapeNarrow =
        viewportWidth > viewportHeight &&
        viewportHeight < LANDSCAPE_PHONE_MAX_HEIGHT;

    const noOtherParticipants = meetingMembers.size < 2;
    // Alone: only the bespoke view's cases that the layout engine doesn't cover
    // still need it — the "no other participants" message (minimized) and the
    // docked corner PiP (self-view hidden from the grid). The normal case (your
    // self-view shown in the grid) falls through to MeetingMainArea below, so a
    // lone self-view is centered and aspect-clamped by the same engine as any
    // other tile instead of filling the pane.
    if (
        noOtherParticipants &&
        !senderScreenShareActive &&
        (isMinimizedView || !shouldShowSenderInGrid)
    ) {
        return (
            <SenderAloneView
                isMinimizedView={isMinimizedView}
                isFullscreenOrNarrow={isFullscreenOrNarrow}
                shouldShowSenderInGrid={shouldShowSenderInGrid}
            />
        );
    }

    if (isMinimizedView) {
        return (
            <div className={twMerge('start-0 flex w-full justify-center')}>
                <MeetingMinimizedMembers
                    ownName={ownName}
                    membersToRender={allMembersToRender}
                    numberOfMembersToRender={
                        shouldShowSenderInGrid
                            ? numberOfMembersToRender - 1
                            : numberOfMembersToRender
                    }
                />
            </div>
        );
    }

    // Everything else — full canvas and the compact dock (thumbnail/sidebar) —
    // lays out from the measured-container layout engine.
    return (
        <MeetingMainArea
            feedMembers={nonScreenSharesToRender}
            presenters={screenSharesToRender ?? []}
            totalFeedCount={totalFeedMemberCount}
            shouldShowSenderInGrid={shouldShowSenderInGrid}
            isCompact={isNarrowWindow || isLandscapeNarrow || isThumbnailView}
            isThumbnail={isThumbnailView}
        />
    );
};

export default MeetingVideoGrid;
